import * as React from 'react';
import styled from 'styled-components';

import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';
import ListItemButton from '@mui/material/ListItemButton';
import IconButton from '@mui/material/IconButton';
import Collapse from '@mui/material/Collapse';
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined';

import { Segment } from 'ff/debrief/models';
import { formatDuration } from 'ff/debrief/utils';
import SegmentScore from 'ff/debrief/components/segments/SegmentScore';
import DownArrowIcon from 'ff/debrief/components/customIcons/DownArrowIcon';

interface Props {
    id: string;
    label: string;
    category?: string;
    startOffsetInSeconds: number;
    startUtc: number;
    durationInSeconds: number;
    color: string;
    distance: string;
    score?: number;
    level: number;
    selectedSegmentId: string;
    isActive: boolean;
    childSegments: Segment[];
    withBorders?: boolean;
    withTopBorders?: boolean;
    onClick?: (segment: Segment) => void;
    isScoreModeEnabled?: boolean;
}

const levelMargins: { [level: number]: string } = {
    0: '0',
    1: '8px',
    2: '24px',
    3: '40px',
    4: '56px',
    5: '72px',
};

const SegmentItem = React.memo(function SegmentItem(
    props: React.PropsWithChildren<Props>
) {
    const {
        onClick,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        children,
        selectedSegmentId,
        isActive,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        withTopBorders,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        withBorders,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        level,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        childSegments,
        isScoreModeEnabled = false,
        ...segment
    } = props;
    const isSelected = selectedSegmentId === segment.id;
    const [isExpanded, setIsExpanded] = React.useState(
        !segment?.label?.includes('Taxi')
    );
    const segmentItemRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const element = segmentItemRef.current;
        if (!isSelected || !element) return;

        const rect = element.getBoundingClientRect();
        const isVisible =
            rect.top >= 0 && rect.bottom <= window.innerHeight - 50;

        if (!isVisible) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [isSelected]);

    const title = segment.label || <Skeleton width={200} />;
    const timestamp =
        segment.startOffsetInSeconds !== undefined ? (
            formatDuration(props.startOffsetInSeconds)
        ) : (
            <Skeleton width={40} />
        );

    const handleClick = () => {
        onClick(segment);
    };

    const handleChevronClick: React.MouseEventHandler<
        HTMLButtonElement
    > = evt => {
        evt.stopPropagation();
        setIsExpanded(!isExpanded);
    };

    const margin = levelMargins[props.level];

    return (
        <Thing
            data-testid="thing-container"
            $withBorder={props.withBorders}
            mb={props.withBorders || props.level === 0 ? 1 : 0}
        >
            <Container
                id={props.id}
                ref={segmentItemRef}
                selected={isActive}
                className={isSelected ? 'selected' : ''}
                onClick={handleClick}
                $withTopBorder={props.withTopBorders}
            >
                <Content marginLeft={margin}>
                    {props.childSegments?.length > 0 && (
                        <IconButton
                            sx={{
                                padding: 0,
                                width: '16px',
                                position: 'relative',
                                right: '8px',
                            }}
                            size="small"
                            onClick={handleChevronClick}
                        >
                            <DownArrowIcon
                                fontSize="inherit"
                                sx={{
                                    transform: isExpanded
                                        ? null
                                        : 'rotate(-90deg)',
                                    fill: isSelected ? 'white' : 'black',
                                }}
                            />
                        </IconButton>
                    )}
                    <Stack flexGrow={1}>
                        <Category>{segment.category}</Category>
                        <Title
                            fontWeight={
                                props.childSegments.length > 0 ? 700 : 400
                            }
                        >
                            {title}
                        </Title>
                    </Stack>
                    {typeof segment.score === 'number' &&
                        (isScoreModeEnabled ? (
                            <SegmentScore score={segment.score} />
                        ) : (
                            <CenteredBox>
                                <AssignmentTurnedInOutlinedIcon />
                            </CenteredBox>
                        ))}
                    <Stack ml={0.2} width="48px">
                        <TimeStamp>{timestamp}</TimeStamp>
                        {props.distance && (
                            <Distance>{props.distance}</Distance>
                        )}
                    </Stack>
                </Content>
            </Container>
            <Collapse in={isExpanded}>
                <div>
                    {props.childSegments.map(({ children, ...s }) => (
                        <SegmentItem
                            key={s.label + s.startOffsetInSeconds}
                            onClick={props.onClick}
                            isActive={props.isActive || isSelected}
                            selectedSegmentId={props.selectedSegmentId}
                            childSegments={children}
                            level={props.level + 1}
                            withTopBorders
                            isScoreModeEnabled={isScoreModeEnabled}
                            {...s}
                        />
                    ))}
                </div>
            </Collapse>
        </Thing>
    );
});

const Thing = styled(Box)<{ $withBorder?: boolean }>`
    border-radius: 8px;
    ${props =>
        props.$withBorder ? 'border: 1px solid rgba(0, 0, 0, 0.30);' : ''}
`;

const Container = styled(ListItemButton)<{ $withTopBorder?: boolean }>`
    position: relative;
    display: flex;
    padding: 0 8px;
    flex-direction: row;
    align-items: stretch;
    ${props =>
        props.$withTopBorder ? 'border-top: 1px solid rgba(0, 0, 0, 0.1);' : ''}
    &.selected {
        background-color: #3498db;
        border-radius: 7px;
        & * {
            color: #ffffff;
        }
    }
`;

const Content = styled(Box)`
    flex-grow: 1;
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 8px 8px 8px 0;
`;

const Category = styled(Typography)`
    color: #646468;
    font-size: 11px;
    font-weight: 400;
`;

const Title = styled(Typography)`
    color: #1c1c1e;
    font-size: 13px;
    line-height: 17px;
`;

const TimeStamp = styled(Typography)`
    color: #697d90;
    font-size: 11px;
    font-weight: 400;
    text-align: right;
`;

const Distance = styled(Typography)`
    color: #697d90;
    font-size: 11px;
    font-weight: 700;
    text-align: right;
`;

const CenteredBox = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px 6px;
    border-radius: 4px;
    width: 27px;
    height: 20px;
    margin-left: 4px;
`;

export default SegmentItem;
